import { Consts } from '../../res';

/**
 * What a package costs at the checkout — the arithmetic, and nothing else.
 *
 * Kept out of the hook so it can be read, and tested, without a component
 * around it. The server charges what it charges; these figures are what the
 * reader is shown before they agree to it, so they have to add up the same way.
 */

const SECONDS_IN_A_DAY = 60 * 60 * 24;

const amount = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

/** A package's id as `/student/subscribe` takes it. */
export const packageIdOf = (pkg) => pkg?.idSubscriptionPackage ?? pkg?.id;

/** How long a subscription to the package lasts, in whole days. */
export const daysOf = (pkg) => Math.round(amount(pkg?.validitySeconds) / SECONDS_IN_A_DAY);

/**
 * What a promotion code takes off this package.
 *
 * A code is either for every package or for one (`subscribePackageId`); on any
 * other package it is worth nothing. It is a flat amount or a percentage, and
 * the percentage is of the price the package is already sold at — after its
 * own discount, not before.
 */
export const promotionDiscount = (promotion, pkg) => {
    const value = amount(promotion?.discountUSD);
    if (!value) return 0;

    const onlyFor = promotion.subscribePackageId;
    if (onlyFor && String(onlyFor) !== String(packageIdOf(pkg))) return 0;

    if (promotion.discountType === Consts.DISCOUNT_TYPES.AMOUNT) return value;

    return (amount(pkg?.priceUSD) - amount(pkg?.discountUSD)) * value / 100;
};

/**
 * The receipt: the list price, what the package's own discount and the code
 * each take off, and what is left to pay.
 *
 * Neither deduction can exceed what it is deducted from, so the lines always
 * sum to the total and the total never goes below nothing.
 */
export const totalsOf = (pkg, codeDiscount = 0) => {
    const price = Math.max(amount(pkg?.priceUSD), 0);
    const discount = Math.min(Math.max(amount(pkg?.discountUSD), 0), price);
    const code = Math.min(Math.max(amount(codeDiscount), 0), price - discount);

    return { price, discount, code, total: price - discount - code };
};

/** "$12.5" — a figure, with no more decimals than it needs. */
export const formatUSD = (value) => `$${Number(amount(value).toFixed(2))}`;
